"use client";

import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Icon } from "@/components/ui/icon";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SubmitButton } from "@/components/ui/button";
import {
  FloatingWindow,
  FloatingWindowBreadcrumbs,
  FloatingWindowContainer,
  FloatingWindowContent,
  FloatingWindowFooter,
  FloatingWindowHeader,
  FloatingWindowTitle,
} from "@/components/ui/floating-window";

import { VariableContextProvider } from "../variable/context";
import { StepEditor } from "@/features/sequences/components/editors/step-editor";
import { LengthRadio } from "../form/length-radio";
import { docToPlainText } from "../../lib/doc-utils";
import { DEFAULT_AI_PROMPT_LENGTH } from "../../lib/length";
import type { AiPromptLength } from "../../lib/types";

const INSTRUCTIONS_HELPER =
  "Provide clear and detailed guidelines for the AI model to ensure the generated output meets all requirements.";
const LENGTH_HELPER =
  "Set the expected number of sentences for the generation result";

const EDITOR_FEATURES = {
  variables: true,
  snippets: false,
  aiPrompts: false,
  writeWithAi: false,
  formatting: true,
};

const EMPTY_INSTRUCTIONS: Record<string, unknown> = {
  type: "doc",
  content: [{ type: "paragraph" }],
};

export interface InlinePromptValue {
  instructions_doc: Record<string, unknown>;
  length_sentences: number;
  label?: string | null;
}

export interface InlinePromptEditorProps {
  mode: "create" | "edit";
  /** Seed for edit mode (the node's current attrs). */
  initialValue?: InlinePromptValue;
  stepLabel?: string;
  stepTypeLabel?: string;
  onClose: () => void;
  /** Caller writes the node attrs from the validated value. */
  onSubmit: (value: InlinePromptValue) => void;
}

const formSchema = z.object({
  label: z.string().trim().max(80).optional().or(z.literal("")),
  length_sentences: z.number().int().min(1).max(5),
  instructions_doc: z.custom<Record<string, unknown>>(
    (value) =>
      !!value &&
      typeof value === "object" &&
      docToPlainText(value as Record<string, unknown>).trim().length > 0,
    { message: "Instructions are required" },
  ),
});

type FormValues = z.infer<typeof formSchema>;

export function InlinePromptEditor({
  mode,
  initialValue,
  stepLabel,
  stepTypeLabel,
  onClose,
  onSubmit,
}: InlinePromptEditorProps) {
  const isEdit = mode === "edit";

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      label: initialValue?.label ?? "",
      length_sentences: initialValue?.length_sentences ?? DEFAULT_AI_PROMPT_LENGTH,
      instructions_doc: initialValue?.instructions_doc ?? EMPTY_INSTRUCTIONS,
    },
  });

  const onValidSubmit = useCallback(
    (values: FormValues) => {
      const label = values.label?.trim();
      onSubmit({
        instructions_doc: values.instructions_doc,
        length_sentences: values.length_sentences,
        label: label && label.length > 0 ? label : null,
      });
      onClose();
    },
    [onClose, onSubmit],
  );

  const canSubmit = form.formState.isValid;

  return (
    <FloatingWindow
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <FloatingWindowContainer className="flex max-h-[80vh] min-h-[60vh] w-[400px] flex-col">
        <FloatingWindowHeader>
          <FloatingWindowTitle>
            <Icon
              name="IconPrompt"
              size={16}
              className="text-[#b45309] dark:text-[#ffdb8e]"
            />
            <span className="bg-clip-text text-[#b45309] dark:bg-gradient-to-r dark:from-[#ffdb8e] dark:via-[#ffdfb2] dark:to-[#ffaf53] dark:text-transparent">
              AI Prompt
            </span>
          </FloatingWindowTitle>
          {(stepLabel || stepTypeLabel) && (
            <FloatingWindowBreadcrumbs
              path={[stepLabel, stepTypeLabel].filter(Boolean) as string[]}
            />
          )}
        </FloatingWindowHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onValidSubmit)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <VariableContextProvider allowBlockEnrollment={false}>
              <FloatingWindowContent className="gap-5">
                {/* Label (optional) */}
                <FormField
                  control={form.control}
                  name="label"
                  render={({ field }) => (
                    <FormItem className="gap-2">
                      <FormLabel className="text-sm font-medium text-foreground">
                        Label
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Opener"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Instructions */}
                <Controller
                  control={form.control}
                  name="instructions_doc"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <span className="text-sm font-medium text-foreground">
                        AI Prompt <span className="text-destructive">*</span>
                      </span>
                      <p className="text-sm text-contrast/60">
                        {INSTRUCTIONS_HELPER}
                      </p>
                      <StepEditor
                        jobId=""
                        sequenceId=""
                        stepType="automatic_email"
                        value={field.value as Record<string, unknown>}
                        onChange={(doc) => field.onChange(doc)}
                        placeholder="Write your prompt instructions…"
                        enabledFeatures={EDITOR_FEATURES}
                        className="min-h-50 max-h-70 overflow-y-auto rounded-lg border border-contrast/15 bg-contrast/6 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
                      />
                    </div>
                  )}
                />

                {/* Ideal length */}
                <FormField
                  control={form.control}
                  name="length_sentences"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-foreground">
                        Ideal length
                      </FormLabel>
                      <p className="-mt-1 text-xs text-contrast/50">
                        {LENGTH_HELPER}
                      </p>
                      <FormControl>
                        <LengthRadio
                          value={field.value as AiPromptLength}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FloatingWindowContent>
            </VariableContextProvider>

            <FloatingWindowFooter className="items-center justify-end">
              <SubmitButton
                type="submit"
                size="sm"
                disabled={!canSubmit}
                variant="ai"
                icon={<Icon name="IconSparklesTwo" size={16} />}
              >
                {isEdit ? "Save & Generate" : "Insert & Generate"}
              </SubmitButton>
            </FloatingWindowFooter>
          </form>
        </Form>
      </FloatingWindowContainer>
    </FloatingWindow>
  );
}
